import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { 
  FiActivity, FiShield, FiClock, FiUser, FiInfo, FiTrash2, 
  FiAlertOctagon, FiCheckCircle, FiUnlock, FiPlus, FiEdit2, FiX, FiRefreshCw 
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function SystemLogsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('logs'); // logs, bans
  const [logs, setLogs] = useState([]);
  const [bans, setBans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // States สำหรับการแบนใหม่
  const [banType, setBanType] = useState('ip');
  const [banValue, setBanValue] = useState('');
  const [banReason, setBanReason] = useState('');

  // States สำหรับการแก้ไขงานด่วนจากหน้า Log
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState({
    customerName: '',
    price: '',
    problem: '',
    status: ''
  });

  // ตรวจสอบสิทธิ์ว่าใช่ไอที (Role: it / username: it) หรือเจ้าของระบบ (Role: owner / username: owner) หรือไม่
  const isITOrOwner = user?.role === 'owner' || user?.role === 'it' || user?.username === 'it' || user?.username === 'owner';

  if (!isITOrOwner) {
    return <Navigate to="/admin/dashboard" />;
  }

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = () => {
    setIsLoading(true);
    if (activeTab === 'logs') {
      fetchLogs();
    } else {
      fetchBans();
    }
  };

  const fetchLogs = () => {
    fetch(`${API_URL}?action=get_logs`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setLogs(data.data);
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching logs:', err);
        setIsLoading(false);
      });
  };

  const fetchBans = () => {
    fetch(`${API_URL}?action=get_banned_list`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setBans(data.data);
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching bans:', err);
        setIsLoading(false);
      });
  };

  // ดึงรหัสงาน (Job ID) จากรายละเอียด Log
  const extractJobId = (details) => {
    if (!details) return null;
    const match = details.match(/\(ID:\s*(\d+)\)/i) || details.match(/\bID\s*(\d+)\b/i);
    return match ? match[1] : null;
  };

  // 🗑️ ลบรายการ Log ตัวนี้ออก
  const handleDeleteLog = (logId) => {
    Swal.fire({
      title: 'ลบประวัติ Log นี้?',
      text: 'ลบเฉพาะบันทึกข้อความ Log บรรทัดนี้ (ข้อมูลในระบบจะไม่ถูกลบ)',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบเลย',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${API_URL}?action=delete_log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: logId })
        })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            Swal.fire('สำเร็จ', 'ลบประวัติ Log เรียบร้อย', 'success');
            fetchLogs();
          }
        });
      }
    });
  };

  // 🗑️ ลบงานซ่อมต้นทางจาก Log
  const handleDeleteJob = (jobId) => {
    Swal.fire({
      title: 'ลบงานซ่อมต้นทาง?',
      text: `คุณต้องการลบข้อมูลคิวงานซ่อมรหัส ID: ${jobId} ออกจากระบบถาวรหรือไม่?`,
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบงานซ่อมถาวร',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${API_URL}?action=delete_job`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: jobId })
        })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            Swal.fire('สำเร็จ', 'ลบงานซ่อมต้นทางออกจากฐานข้อมูลแล้ว', 'success');
            fetchLogs();
          } else {
            Swal.fire('ข้อผิดพลาด', 'ไม่พบงานซ่อมนี้ หรือถูกลบไปแล้ว', 'error');
          }
        });
      }
    });
  };

  // ✏️ เปิดฟอร์มแก้ไขงานต้นทาง
  const handleEditJobClick = (jobId) => {
    Swal.fire({ title: 'กำลังค้นหาคิวงาน...', didOpen: () => Swal.showLoading() });
    
    fetch(`${API_URL}?action=get_all_jobs`)
      .then(res => res.json())
      .then(data => {
        Swal.close();
        if (data.success && data.data) {
          const found = data.data.find(j => String(j.id) === String(jobId));
          if (found) {
            setEditingJob(found);
            setEditJobForm({
              customerName: found.customerName || '',
              price: found.price || '',
              problem: found.problem || '',
              status: found.status || 'pending_approval'
            });
          } else {
            Swal.fire('ไม่พบข้อมูล', 'ไม่พบข้อมูลงานซ่อมรหัสนี้ในระบบ (อาจถูกลบไปแล้ว)', 'warning');
          }
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อฐานข้อมูลได้', 'error');
      });
  };

  // 💾 บันทึกแก้ไขงานซ่อม
  const handleUpdateJobSubmit = (e) => {
    e.preventDefault();
    if (!editingJob) return;

    Swal.fire({ title: 'กำลังบันทึก...', didOpen: () => Swal.showLoading() });

    fetch(`${API_URL}?action=update_job`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: editingJob.id,
        ...editJobForm
      })
    })
    .then(res => res.json())
    .then(data => {
      Swal.close();
      if (data.success) {
        Swal.fire('สำเร็จ!', 'แก้ไขข้อมูลงานซ่อมต้นทางเรียบร้อยแล้ว', 'success');
        setEditingJob(null);
        fetchLogs();
      } else {
        Swal.fire('ผิดพลาด', 'บันทึกไม่สำเร็จ', 'error');
      }
    });
  };

  // 🚫 สั่งแบนด่วน (จากปุ่มในตาราง Log)
  const handleQuickBan = (type, value) => {
    Swal.fire({
      title: `สั่งแบนความปลอดภัย?`,
      text: `คุณต้องการสั่งระงับ (Ban) ${type === 'ip' ? 'ที่อยู่ IP' : 'ชื่อผู้ใช้'}: ${value} หรือไม่?`,
      icon: 'warning',
      input: 'text',
      inputLabel: 'ระบุเหตุผลในการแบน',
      inputValue: 'พบพฤติกรรมคีย์ข้อมูลไม่เหมาะสม / ต้องสงสัยภัยคุกคาม',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'ยืนยันสั่งแบน',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${API_URL}?action=add_ban`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type,
            value,
            reason: result.value || 'ไม่ระบุเหตุผล',
            createdBy: user?.name || 'IT Support'
          })
        })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            Swal.fire('สำเร็จ', 'ระงับสิทธิ์เข้าใช้งานสำเร็จแล้ว', 'success');
          } else {
            Swal.fire('ข้อมูลซ้ำ', data.message, 'info');
          }
        });
      }
    });
  };

  // 🚫 สั่งแบนจากฟอร์มปกติ (หน้า Tab 2)
  const handleFormBanSubmit = (e) => {
    e.preventDefault();
    if (!banValue.trim()) return Swal.fire('แจ้งเตือน', 'กรุณากรอกไอดี หรือ IP ที่ต้องการแบน', 'warning');

    fetch(`${API_URL}?action=add_ban`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: banType,
        value: banValue.trim(),
        reason: banReason || 'ถูกระงับสิทธิ์การใช้งานจากฝ่ายเทคนิค',
        createdBy: user?.name || 'IT Support'
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        Swal.fire('สำเร็จ', 'สั่งแบนข้อมูลเรียบร้อยแล้ว', 'success');
        setBanValue('');
        setBanReason('');
        fetchBans();
      } else {
        Swal.fire('ล้มเหลว', data.message, 'error');
      }
    });
  };

  // 🔓 ปลดแบน (Remove Ban)
  const handleRemoveBan = (banId, value) => {
    Swal.fire({
      title: 'ปลดล็อกการแบน?',
      text: `คุณต้องการอนุญาตให้ ${value} กลับมาเข้าใช้งานระบบได้ปกติหรือไม่?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'ปลดแบนใช้งานได้',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${API_URL}?action=remove_ban`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: banId })
        })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            Swal.fire('ปลดล็อกเรียบร้อย', 'กู้คืนสิทธิ์เสร็จสิ้น', 'success');
            fetchBans();
          }
        });
      }
    });
  };

  const getActionBadge = (action) => {
    const actionStr = action.toLowerCase();
    if (actionStr.includes('login')) return <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-md text-[10px] font-bold border border-green-200">🟢 {action}</span>;
    if (actionStr.includes('delete') || actionStr.includes('ban')) return <span className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-md text-[10px] font-bold border border-red-200">🔴 {action}</span>;
    if (actionStr.includes('create') || actionStr.includes('add')) return <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-md text-[10px] font-bold border border-blue-200">🔵 {action}</span>;
    return <span className="px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-md text-[10px] font-bold border border-gray-200">{action}</span>;
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-gray-100">
      
      {/* ส่วนหัวหน้าจอ */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-800 dark:text-white flex items-center gap-2">
            <FiShield className="text-red-500" /> แผงควบคุมความปลอดภัยและล็อกระบบ <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full ml-2">IT & OWNER ONLY</span>
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            บันทึกการทำงานของพนักงาน ตรวจสอบการคีย์งานผิดพลาด และระบบบล็อกความปลอดภัยจากภัยคุกคาม
          </p>
        </div>
        <button 
          onClick={loadData}
          className="p-2 bg-gray-100 dark:bg-[#1e293b] hover:bg-gray-200 dark:hover:bg-[#334155] rounded-xl flex items-center gap-1 text-xs font-bold transition shrink-0"
        >
          <FiRefreshCw size={12} className={isLoading ? 'animate-spin' : ''} /> รีเฟรชข้อมูล
        </button>
      </div>

      {/* แถบเปลี่ยนแท็บ */}
      <div className="flex border-b border-gray-200 dark:border-[#334155] pb-1">
        <button 
          onClick={() => setActiveTab('logs')}
          className={`pb-3 text-sm font-bold transition-all relative px-4 ${activeTab === 'logs' ? 'text-indigo-600 dark:text-indigo-400 font-extrabold border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-400 hover:text-gray-600'}`}
        >
          📋 บันทึกประวัติการกระทำ (Logs)
        </button>
        <button 
          onClick={() => setActiveTab('bans')}
          className={`pb-3 text-sm font-bold transition-all relative px-4 ${activeTab === 'bans' ? 'text-indigo-600 dark:text-indigo-400 font-extrabold border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-400 hover:text-gray-600'}`}
        >
          🛡️ ควบคุมการแบนบัญชี & IP (Banned List)
        </button>
      </div>

      {/* 1. แท็บประวัติบันทึก Log */}
      {activeTab === 'logs' && (
        <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
              <thead className="text-xs uppercase bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155] transition-colors">
                <tr>
                  <th className="px-4 py-4 font-medium w-36"><div className="flex items-center gap-1"><FiClock /> วันเวลา</div></th>
                  <th className="px-4 py-4 font-medium w-48"><div className="flex items-center gap-1"><FiUser /> ผู้กระทำ (IP)</div></th>
                  <th className="px-4 py-4 font-medium w-32"><div className="flex items-center gap-1"><FiActivity /> การกระทำ</div></th>
                  <th className="px-4 py-4 font-medium"><div className="flex items-center gap-1"><FiInfo /> รายละเอียดเหตุการณ์</div></th>
                  <th className="px-4 py-4 font-medium text-center w-52 min-w-[200px]">ตัวช่วยจัดการข้อมูล</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
                {isLoading ? (
                  <tr><td colSpan="5" className="text-center py-14 font-bold text-blue-500">กำลังตรวจสอบบันทึก Log...</td></tr>
                ) : logs.length > 0 ? (
                  logs.map((log) => {
                    const jobId = extractJobId(log.details);
                    return (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-[#0f172a]/30 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          {log.createdAt}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5 whitespace-nowrap">
                            {log.user}
                            {log.user !== 'System' && log.user !== 'Customer' && (
                              <button 
                                onClick={() => handleQuickBan('username', log.user)}
                                className="text-[9px] font-black bg-red-50 hover:bg-red-100 text-red-500 border border-red-200 px-1.5 py-0.5 rounded-full transition"
                                title="แบนผู้ใช้นี้"
                              >
                                แบนไอดี
                              </button>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5 flex items-center gap-1.5">
                            {log.ip_address || 'Localhost'}
                            {log.ip_address && log.ip_address !== '127.0.0.1' && (
                              <button 
                                onClick={() => handleQuickBan('ip', log.ip_address)}
                                className="text-[8px] bg-red-600 hover:bg-red-700 text-white px-1 rounded transition font-bold"
                                title="แบน IP"
                              >
                                แบน IP
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                          {log.details}
                        </td>
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-center items-center gap-2">
                            {/* หากเจอรหัสงานซ่อม แสดงปุ่มให้กดเข้าไปแก้ไขหรือลบงานได้ */}
                            {jobId && (
                              <>
                                <button 
                                  onClick={() => handleEditJobClick(jobId)}
                                  className="px-3 py-1 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white rounded-full text-[10px] font-bold transition flex items-center gap-1 shadow-sm whitespace-nowrap"
                                  title="แก้ไขงานซ่อมนี้"
                                >
                                  <FiEdit2 size={10} /> แก้ไขงาน
                                </button>
                                <button 
                                  onClick={() => handleDeleteJob(jobId)}
                                  className="px-3 py-1 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white rounded-full text-[10px] font-bold transition flex items-center gap-1 shadow-sm whitespace-nowrap"
                                  title="ลบงานซ่อมต้นทาง"
                                >
                                  <FiX size={10} /> ลบงาน
                                </button>
                              </>
                            )}
                            <button 
                              onClick={() => handleDeleteLog(log.id)}
                              className="text-gray-400 hover:text-red-500 p-1 rounded transition"
                              title="ลบข้อความ Log นี้"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan="5" className="text-center py-10 text-gray-500 font-bold">ยังไม่มีบันทึกประวัติการกระทำในระบบ</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. แท็บรายการการแบนและจัดการความปลอดภัย */}
      {activeTab === 'bans' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* ฝั่งซ้าย: ฟอร์มสั่งแบนความปลอดภัยใหม่ */}
          <div className="lg:col-span-1">
            <form onSubmit={handleFormBanSubmit} className="bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm space-y-4 text-left">
              <h3 className="font-black text-sm text-red-500 uppercase tracking-wider flex items-center gap-1">
                <FiAlertOctagon /> สั่งระงับสิทธิ์เข้าใช้งานใหม่
              </h3>
              
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400">ประเภทการแบน</label>
                <select 
                  value={banType}
                  onChange={e => setBanType(e.target.value)}
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs font-bold"
                >
                  <option value="ip">🌐 แบนตามเลข IP Address</option>
                  <option value="username">👤 แบนตามไอดีบัญชี (Username)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400">
                  {banType === 'ip' ? 'เลข IP Address ที่ต้องการระงับ' : 'ชื่อไอดี Username ที่ต้องการระงับ'}
                </label>
                <input 
                  type="text" 
                  placeholder={banType === 'ip' ? 'เช่น 159.22.34.102' : 'เช่น test_admin'}
                  value={banValue}
                  onChange={e => setBanValue(e.target.value)}
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs outline-none focus:border-red-500"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400">เหตุผลในการระงับสิทธิ์</label>
                <textarea 
                  placeholder="เหตุผลความมั่นคงปลอดภัย เช่น สแปมข้อมูล, เข้าระบบโดยไม่ได้รับอนุญาต..."
                  value={banReason}
                  onChange={e => setBanReason(e.target.value)}
                  rows="3"
                  className="w-full p-2 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-lg text-xs outline-none focus:border-red-500 resize-none"
                />
              </div>

              <button 
                type="submit"
                className="w-full py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-xs shadow-md transition flex items-center justify-center gap-1"
              >
                <FiAlertOctagon size={14} /> 🚫 สั่งแบนการเข้าถึงทันที
              </button>
            </form>
          </div>

          {/* ฝั่งขวา: รายชื่อผู้ถูกระงับสิทธิ์ในฐานข้อมูล */}
          <div className="lg:col-span-2 bg-white dark:bg-[#1e293b] p-5 rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm space-y-4">
            <h3 className="font-black text-sm text-gray-800 dark:text-white uppercase tracking-wider flex items-center gap-1 text-left">
              🛡️ บัญชี & IP ที่ถูกบล็อกในระบบ ({bans.length})
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
                <thead className="text-xs uppercase bg-gray-50 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155]">
                  <tr>
                    <th className="px-3 py-3 w-28">ประเภท</th>
                    <th className="px-3 py-3">ข้อมูลที่ระงับ</th>
                    <th className="px-3 py-3">เหตุผล</th>
                    <th className="px-3 py-3 w-28">ผู้แบน / วันเวลา</th>
                    <th className="px-3 py-3 text-center w-24">ปลดแบน</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-[#334155] text-xs">
                  {bans.length > 0 ? (
                    bans.map((ban) => (
                      <tr key={ban.id} className="hover:bg-red-50/20 dark:hover:bg-red-950/10">
                        <td className="px-3 py-3 font-bold">
                          {ban.type === 'ip' ? (
                            <span className="text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">🌐 IP Address</span>
                          ) : (
                            <span className="text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">👤 Username</span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-mono font-bold text-red-600 dark:text-red-400">
                          {ban.value}
                        </td>
                        <td className="px-3 py-3 text-gray-500">
                          {ban.reason}
                        </td>
                        <td className="px-3 py-3 text-gray-400 leading-tight">
                          {ban.createdBy}
                          <p className="text-[10px] mt-0.5">{new Date(ban.createdAt).toLocaleDateString('th-TH')}</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button 
                            onClick={() => handleRemoveBan(ban.id, ban.value)}
                            className="bg-green-50 hover:bg-green-100 text-green-600 p-1.5 rounded-lg border border-green-200 transition"
                            title="ปลดระงับการแบน"
                          >
                            <FiUnlock size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-10 text-gray-400 font-semibold">ไม่มีการแบนใดๆ ในประวัติระบบความปลอดภัย</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* 3. MODAL ป๊อปอัปแก้ไขงานซ่อมด่วนผ่านหน้า Log */}
      {editingJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in text-gray-800">
          <div className="bg-white dark:bg-[#131825] dark:text-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden">
            
            <div className="bg-yellow-50 dark:bg-[#1e2330] p-4 flex justify-between items-center border-b border-gray-200 dark:border-[#334155]">
              <h3 className="font-black text-gray-800 dark:text-white text-sm flex items-center gap-1.5">
                <span>✏️ แก้ไขคิวงานด่วนจาก Log:</span>
                <span className="text-indigo-600 dark:text-indigo-400">ID {editingJob.id}</span>
              </h3>
              <button 
                onClick={() => setEditingJob(null)} 
                className="text-gray-500 hover:text-red-500 transition p-1"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleUpdateJobSubmit} className="p-5 space-y-4 text-left">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">ชื่อลูกค้าผู้ใช้บริการ</label>
                <input 
                  type="text" 
                  value={editJobForm.customerName}
                  onChange={e => setEditJobForm({...editJobForm, customerName: e.target.value})}
                  className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs outline-none"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">ราคางานบริการ (บาท)</label>
                <input 
                  type="number" 
                  value={editJobForm.price}
                  onChange={e => setEditJobForm({...editJobForm, price: e.target.value})}
                  className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs outline-none text-right"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">รายละเอียดปัญหา / อาการขัดข้อง</label>
                <textarea 
                  value={editJobForm.problem}
                  onChange={e => setEditJobForm({...editJobForm, problem: e.target.value})}
                  rows="3"
                  className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs outline-none resize-none"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400">สถานะคิวงานซ่อม</label>
                <select 
                  value={editJobForm.status}
                  onChange={e => setEditJobForm({...editJobForm, status: e.target.value})}
                  className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs font-bold"
                >
                  <option value="pending_approval">รอการตอบรับการจอง</option>
                  <option value="assigned">จ่ายงานสำเร็จ (ช่างเตรียมเดินทาง)</option>
                  <option value="in_progress">กำลังเข้าบริการแก้ไข</option>
                  <option value="completed">เสร็จสิ้นงาน (ปิดคิวบริการแล้ว)</option>
                  <option value="cancelled">ยกเลิกคิวงานซ่อม</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="button"
                  onClick={() => setEditingJob(null)}
                  className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 font-bold rounded-lg text-xs transition"
                >
                  ยกเลิก
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold rounded-lg text-xs shadow transition"
                >
                  บันทึกการแก้ไขด่วน
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}